import React, { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSupportContext } from '../../context/SupportContext';
import { FundingWorkspaceProvider, useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { FundGeniusSidebarProvider, useFundGeniusSidebar } from '../../context/FundGeniusSidebarContext';
import { usePlatformAgencies } from '../../hooks/funding/usePlatformAgencies';
import { getPlatformAgencyId, getPlatformAgencyLabel } from '../../services/platform/platformAgenciesService';
import { FUNDGENIUS_HQ_PRODUCT_NAME } from '../../utils/funding/constants';
import {
  filterFundGeniusNavByAccess,
  getAgencyTypeLabel,
  isFundGeniusAgency,
} from '../../utils/agencyType';
import {
  BACK_TO_PLATFORM_GUARD_KEY,
  navigateBackToPlatform,
} from '../../utils/backToPlatformNavigation';
import { FUNDGENIUS_PRIMARY_NAV } from './fundingNav';
import FundGeniusWorkspaceGate from '../../components/funding/FundGeniusWorkspaceGate';
import NotificationDropdown from '../../components/notifications/NotificationDropdown';
import ProfileMenuDropdown from '../../components/profile/ProfileMenuDropdown';
import { getThemeTokens } from '../SelfServePortalLayout';
import { WORKSPACE_PRODUCTS } from '../../config/productWorkspaceConfig';
import './fundgenius-hq.css';

const FundGeniusHQLayoutShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { exitSupportMode, isInSupportMode } = useSupportContext();
  const {
    appearanceMode,
    resolvedMode,
    setAppearanceMode,
  } = useTheme();
  const tokens = useMemo(() => getThemeTokens(resolvedMode), [resolvedMode]);
  const { collapsed, toggleCollapsed } = useFundGeniusSidebar();
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [backNavigating, setBackNavigating] = useState(false);
  const isVisuallyExpanded = !collapsed || hoverExpanded;
  const {
    isPlatformContext,
    isAgencyContext,
    workspaceAgencyId,
    setWorkspaceAgencyId,
    pathFor,
    agencyId,
  } = useFundingWorkspace();

  const {
    agencies,
    loading: agenciesLoading,
    error: agenciesError,
  } = usePlatformAgencies({
    enabled: isPlatformContext,
    token,
    workspaceAgencyId,
    setWorkspaceAgencyId,
  });

  const selectedAgency = agencies.find((agency) => getPlatformAgencyId(agency) === workspaceAgencyId)
    || (agencies.length === 1 ? agencies[0] : null);
  const navItems = filterFundGeniusNavByAccess(FUNDGENIUS_PRIMARY_NAV, selectedAgency);

  const agencyBackPath = agencyId
    ? `/agency/${agencyId}/dashboard`
    : '/dashboard';

  const backLabel = isPlatformContext ? 'Back to Platform' : 'Back to Agency';

  const clearBackGuard = useCallback(() => {
    try {
      sessionStorage.removeItem(BACK_TO_PLATFORM_GUARD_KEY);
    } catch {
      // ignore
    }
  }, []);

  const handleBackToPlatform = useCallback(async (event) => {
    event.preventDefault();
    if (backNavigating) return;
    setBackNavigating(true);
    try {
      // Clear selected agency from URL/session before leaving FundGenius.
      if (typeof setWorkspaceAgencyId === 'function') {
        setWorkspaceAgencyId('');
      }
      await navigateBackToPlatform({
        navigate,
        exitSupportMode: isInSupportMode ? exitSupportMode : undefined,
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        from: `${location.pathname}${location.search || ''}`,
      });
    } finally {
      setBackNavigating(false);
    }
  }, [
    backNavigating,
    exitSupportMode,
    isInSupportMode,
    location.pathname,
    location.search,
    navigate,
    setWorkspaceAgencyId,
  ]);

  const isActive = (segment) => {
    const target = pathFor(segment);
    if (!segment) {
      return location.pathname === target;
    }
    return location.pathname === target || location.pathname.startsWith(`${target}/`);
  };

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const handleThemeChange = useCallback((value) => {
    setAppearanceMode(value);
  }, [setAppearanceMode]);

  return (
    <div
      className={`fundgenius-hq-shell fundgenius-shell flex h-screen max-h-dvh overflow-hidden ${collapsed ? 'sidebar-collapsed fundgenius-shell--rail' : ''}`}
      data-testid="fundgenius-hq-layout"
      data-product={WORKSPACE_PRODUCTS.FUNDGENIUS_HQ}
    >
      <aside
        className={`fundgenius-hq-sidebar fg-dark-sidebar fundgenius-sidebar hidden h-full min-h-0 shrink-0 flex-col overflow-hidden lg:flex ${collapsed ? 'collapsed fundgenius-sidebar--rail' : ''} ${collapsed && hoverExpanded ? 'is-hover-expanded' : ''}`}
        data-testid="fundgenius-hq-sidebar"
        onMouseEnter={() => {
          if (collapsed) setHoverExpanded(true);
        }}
        onMouseLeave={() => setHoverExpanded(false)}
        onFocusCapture={() => {
          if (collapsed) setHoverExpanded(true);
        }}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setHoverExpanded(false);
          }
        }}
      >
        <div className="fundgenius-sidebar__inner flex h-full min-h-0 flex-col overflow-hidden">
          <div className="fg-sidebar-brand fundgenius-sidebar__brand-row">
            <div className="fundgenius-sidebar__brand-left">
              <div className="fundgenius-hq-brand-mark">FG</div>
              {isVisuallyExpanded ? (
                <div className="fg-brand-text fundgenius-sidebar__brand-copy">
                  <div className="text-sm font-bold">{FUNDGENIUS_HQ_PRODUCT_NAME}</div>
                  <div className="text-xs opacity-80">
                    {isPlatformContext
                      ? 'Platform console'
                      : isAgencyContext
                        ? 'Agency workspace'
                        : 'Funding OS'}
                  </div>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="fundgenius-sidebar__collapse-button fg-sidebar-collapse-toggle"
              data-testid="sidebar-collapse-toggle"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
            >
              {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
            </button>
          </div>

          <nav
            className={`fundgenius-sidebar__nav min-h-0 overflow-x-hidden overflow-y-auto p-3 ${isVisuallyExpanded ? 'flex-1 space-y-1' : 'fundgenius-sidebar__nav--rail'}`}
            aria-label="FundGenius HQ"
            data-testid="fundgenius-hq-sidebar-nav"
          >
            {isVisuallyExpanded ? (
              <div className="fundgenius-sidebar__section-label px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] opacity-70">
                Funding OS
              </div>
            ) : null}
            {navItems.map((item) => (
              <div key={item.id}>
                <Link
                  to={pathFor(item.segment)}
                  className="fundgenius-sidebar__nav-item fundgenius-hq-nav-item fg-dark-nav-item"
                  data-active={isActive(item.segment)}
                  data-testid={`fundgenius-nav-${item.id}`}
                  title={item.label}
                  onClick={clearBackGuard}
                >
                  <item.icon size={18} strokeWidth={1.75} />
                  {isVisuallyExpanded ? <span className="fundgenius-sidebar__nav-label flex-1">{item.label}</span> : null}
                  {isVisuallyExpanded && item.badge ? (
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold">
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
                {isVisuallyExpanded && item.children?.length ? (
                  <div className="fundgenius-hq-subnav fg-dark-subnav fundgenius-sidebar__subnav" data-testid={`fundgenius-subnav-${item.id}`}>
                    {item.children.map((child) => (
                      <Link
                        key={child.id}
                        to={pathFor(child.segment)}
                        className="fundgenius-hq-subnav-item"
                        data-active={isActive(child.segment)}
                        data-testid={`fundgenius-nav-${child.id}`}
                        onClick={clearBackGuard}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </nav>

          <div className="fundgenius-sidebar__footer border-t border-white/10 p-3">
            {isPlatformContext ? (
              <button
                type="button"
                onClick={handleBackToPlatform}
                disabled={backNavigating}
                className="fundgenius-sidebar__nav-item fundgenius-hq-nav-item fg-dark-nav-item w-full text-left"
                data-testid="fundgenius-back"
                title={backLabel}
              >
                <ArrowLeft size={18} strokeWidth={1.75} />
                {isVisuallyExpanded ? <span className="fundgenius-sidebar__nav-label">{backLabel}</span> : null}
              </button>
            ) : (
              <Link
                to={agencyBackPath}
                className="fundgenius-sidebar__nav-item fundgenius-hq-nav-item fg-dark-nav-item"
                data-testid="fundgenius-back"
                title={backLabel}
              >
                <ArrowLeft size={18} strokeWidth={1.75} />
                {isVisuallyExpanded ? <span className="fundgenius-sidebar__nav-label">{backLabel}</span> : null}
              </Link>
            )}
          </div>
        </div>
      </aside>

      <div
        className="fundgenius-hq-main-shell flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-testid="fundgenius-hq-main-shell"
      >
        <header
          className="relative z-30 flex shrink-0 flex-wrap items-center gap-3 border-b border-[var(--fg-line)] bg-white/70 px-4 py-3 backdrop-blur-md lg:px-6 dark:bg-[rgba(11,31,24,0.72)]"
          data-testid="fundgenius-hq-header"
        >
          {isPlatformContext ? (
            <div className="flex min-w-[220px] flex-col gap-1">
              <div className="flex items-center gap-2 rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2 text-sm dark:bg-[rgba(11,31,24,0.65)]">
                <Building2 className="size-4 text-[var(--fg-muted)]" />
                <select
                  value={workspaceAgencyId || ''}
                  onChange={(event) => setWorkspaceAgencyId(event.target.value)}
                  className="w-full bg-transparent text-sm text-[var(--fg-text)] outline-none"
                  aria-label="Select agency workspace"
                  data-testid="fundgenius-agency-select"
                  disabled={agenciesLoading}
                >
                  <option value="">{agenciesLoading ? 'Loading agencies…' : 'All agencies'}</option>
                  {agencies.map((agency) => {
                    const optionAgencyId = getPlatformAgencyId(agency);
                    const typeSuffix = isFundGeniusAgency(agency)
                      ? ` · ${getAgencyTypeLabel(agency)}`
                      : '';
                    return (
                      <option key={optionAgencyId} value={optionAgencyId}>
                        {getPlatformAgencyLabel(agency)}{typeSuffix}
                      </option>
                    );
                  })}
                </select>
              </div>
              {agenciesError ? (
                <div
                  className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200"
                  data-testid="fundgenius-agency-load-error"
                >
                  {agenciesError}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--fg-muted)]" />
            <input
              type="search"
              placeholder="Search clients, lenders, documents..."
              className="w-full rounded-xl border border-[var(--fg-line)] bg-white py-2.5 pl-10 pr-3 text-sm outline-none ring-[var(--fg-accent)] focus:ring-2 dark:bg-[rgba(11,31,24,0.65)]"
              aria-label="Search FundGenius HQ"
            />
          </div>
          <div className="rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2 text-xs font-medium text-[var(--fg-muted)] dark:bg-[rgba(11,31,24,0.65)]">
            {isPlatformContext ? 'FundGenius HQ platform workspace' : isAgencyContext ? 'FundGenius HQ workspace' : 'FundGenius HQ'}
          </div>

          <div className="ml-auto flex items-center gap-2" data-testid="fundgenius-header-actions">
            <NotificationDropdown
              tokens={tokens}
              product={WORKSPACE_PRODUCTS.FUNDGENIUS_HQ}
            />
            <ProfileMenuDropdown
              user={user}
              tokens={tokens}
              onLogout={handleLogout}
              resolvedTheme={resolvedMode}
              theme={appearanceMode}
              onThemeChange={handleThemeChange}
              product={WORKSPACE_PRODUCTS.FUNDGENIUS_HQ}
              managePlanPath={pathFor('settings')}
            />
          </div>
        </header>

        <main
          className="fundgenius-hq-main-content min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 lg:p-6"
          data-testid="fundgenius-hq-main-content"
        >
          <FundGeniusWorkspaceGate>
            <Outlet />
          </FundGeniusWorkspaceGate>
        </main>
      </div>
    </div>
  );
};

const FundGeniusHQLayout = () => (
  <FundingWorkspaceProvider>
    <FundGeniusSidebarProvider>
      <FundGeniusHQLayoutShell />
    </FundGeniusSidebarProvider>
  </FundingWorkspaceProvider>
);

export default FundGeniusHQLayout;
